import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { useMutation } from 'Data/Api'
import { useDataValue } from 'Simple/Data'
import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { RELATIONSHIPS } from 'Data/constants.js'
import { email } from 'Data/validate.js'
import { useClient } from 'Data/Api.js'
import query from './query.graphql.js'
import mutation_new_contact_method_association from './newContactMethodAssociationMutation.graphql.js'
import mutation_add_person_contact_methods from './addPersonContactMethodsMutation.graphql.js'
import mutation_update_person_relationships from './updatePersonRelationshipsMutation.graphql.js'

export default function useDataOnSubmit(props, data) {
  let onActionAddNewRelations = useDataOnActionAddNewRelations(props)
  let onActionNewEmailForRelative = useDataOnActionNewEmailForRelative(props)
  let onActionEmailForRelative = useDataOnActionEmailForRelative(props)
  let onActionEmailFromRelative = useDataOnActionEmailFromRelative(props)
  let onActionFormSubmit = useDataOnActionFormSubmit(props)

  return async function onSubmit({ value, args }) {
    switch (args?.type) {
      case 'add_new_relations': {
        return onActionAddNewRelations({ value, args })
      }
      case 'new_email_for_relative': {
        return onActionNewEmailForRelative({ value, args })
      }
      case 'email_for_relative': {
        return onActionEmailForRelative({ value, args })
      }
      case 'form_submit': {
        return onActionFormSubmit({ value, args })
      }
      default: {
        onActionEmailFromRelative({ value, args })
      }
    }
  }
}

function useDataOnActionAddNewRelations(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()
  let [, executeMutationUpdatePersonRelationships] = useMutation(
    mutation_update_person_relationships
  )
  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.id',
  })
  let person_gender = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.gender',
  })
  return async function onAction({ value: email, args }) {
    let relations = args.relations.map(rel => ({
      from_person: {
        id: rel.id,
        type: rel.type,
        role: rel.role,
        permitted_to_see_info: rel.role === 'RESPONSIBLE',
        contact_methods: [],
      },
      to_person: {
        id: person_id,
        type: RELATIONSHIPS.find(r => r.id === rel.type)[person_gender],
        role:
          rel.role === 'RESPONSIBLE'
            ? 'DEPENDENT'
            : rel.role === 'DEPENDENT'
            ? 'RESPONSIBLE'
            : 'OTHER',
        permitted_to_see_info: rel.role === 'DEPENDENT',
        contact_methods: [
          {
            dtype: 'email',
            description: email.description,
            data: {
              address: email.email.address,
            },
          },
        ],
      },
    }))

    let mutationUpdatePersonRelationshipsResponse =
      await executeMutationUpdatePersonRelationships({
        relationships: relations,
      })
    if (mutationUpdatePersonRelationshipsResponse.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Email saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function useDataOnActionNewEmailForRelative(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationAddPersonContactMethods] = useMutation(
    mutation_add_person_contact_methods
  )

  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'edit_relation',
    path: 'from_person',
  })

  return async function onAction({ value: new_email }) {
    // if the email doesn't exist,
    // and we don't want to add a relation,
    // we just save the new email and add
    // a new association
    let mutationAddPersonContactMethods =
      await executeMutationAddPersonContactMethods({
        person_id,
        contact_methods: [
          {
            dtype: 'email',
            description: new_email.description,
            data: {
              address: new_email.email.address,
            },
          },
        ],
      })

    if (mutationAddPersonContactMethods.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Email saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function useDataOnActionFormSubmit(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationAddPersonContactMethods] = useMutation(
    mutation_add_person_contact_methods
  )

  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'edit_relation',
    path: 'from_person',
  })

  let client = useClient()
  return async function onAction({ value: new_email }) {
    // if the email doesn't exist,
    // and we don't want to add a relation,
    // we just save the new email and add
    // a new association

    if (!email(new_email.email.address)) {
      return true
    }

    let { data } = await client
      .query(query, {
        address: new_email.email.address,
      })
      .toPromise()

    if (data?.vaxiom_contact_emails.length > 0) {
      setFlowTo(normalizePath(props.viewPath, 'SearchForEmail/Content'))
      return
    }

    let mutationAddPersonContactMethods =
      await executeMutationAddPersonContactMethods({
        person_id,
        contact_methods: [
          {
            dtype: 'email',
            description: new_email.description,
            data: {
              address: new_email.email.address,
            },
          },
        ],
      })

    if (mutationAddPersonContactMethods.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Email saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function useDataOnActionEmailForRelative(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationNewContactMethodAssociation] = useMutation(
    mutation_new_contact_method_association
  )
  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'edit_relation',
    path: 'from_person',
  })
  return async function onAction({ args }) {
    // if we found that the email already exists,
    // but we don't want to add a realtionship,
    // we just link the patient to the existing contact_method
    let mutationNewContactMethodAssociationRespone =
      await executeMutationNewContactMethodAssociation({
        contact_method_id: args.contact_method_id,
        person_id,
      })

    if (mutationNewContactMethodAssociationRespone.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Email saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function useDataOnActionEmailFromRelative(props) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationNewContactMethodAssociation] = useMutation(
    mutation_new_contact_method_association
  )

  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'edit_relation',
    path: 'from_person',
  })
  return async function onAction({ value: email }) {
    // if we select a relative's email we save it
    let mutationNewContactMethodAssociationRespone =
      await executeMutationNewContactMethodAssociation({
        contact_method_id: email.contact_method_id,
        person_id,
      })

    if (mutationNewContactMethodAssociationRespone.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    notify(notifySuccess('Email saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}
